// src/lib/finishSelection.ts
//
// Pure rules behind the admin "Finish" selector and the "Re-run assembly" action on
// AdminProductEdit. Kept out of the component so they can be tested without React.
//
// Why a finish and not a texture: for native-CAD products (geometry_source dxf_extrusion /
// rfa_cad) process-glb-assembly cannot use a photo-derived texture (CAD UVs never match a
// product photo), so it paints one scalar finish instead. Resolution order in the Lambda:
//   1. products.finish_id -> finish_library row (needs material_source = 'swatch_library')
//   2. consumer_specs_json finish_options / colors_available recognised names ("Matte Black")
//   3. a flagged neutral grey
// Picking a finish here is option 1.

export const SWATCH_MATERIAL_SOURCE = "swatch_library";
export const NATIVE_CAD_GEOMETRY_SOURCES = ["dxf_extrusion", "rfa_cad"] as const;

/**
 * trigger-glb-assembly refuses unless PBR is in one of these states (see that function), and
 * returns 409 while an assembly is already pending/processing.
 */
export const ASSEMBLY_PBR_STATES = ["complete", "complete_fallback", "review"] as const;
export const ASSEMBLY_BUSY_STATES = ["pending", "processing"] as const;

export interface FinishOption {
  id: string;
  name: string;
  hex: string;
  category: string | null;
  manufacturer_id: string | null;
}

export function isNativeCad(geometrySource: string | null | undefined): boolean {
  return !!geometrySource && (NATIVE_CAD_GEOMETRY_SOURCES as readonly string[]).includes(geometrySource);
}

/** Label for the dropdown: name, colour and class, e.g. "Black (#1C1C1E, powder_coat)". */
export function finishOptionLabel(f: FinishOption): string {
  const extra = [f.hex, f.category].filter(Boolean).join(", ");
  return `${f.name}${extra ? ` (${extra})` : ""}${f.manufacturer_id ? " — manufacturer finish" : ""}`;
}

/**
 * The products PATCH for choosing (or clearing) a finish.
 *   choose -> finish_id + material_source = 'swatch_library'
 *   clear  -> finish_id = null, and material_source back to null, but ONLY if it is currently
 *             'swatch_library'. A different source (e.g. parametric_generated) is never touched
 *             by a clear: this action did not set it.
 */
export function finishPatch(
  newFinishId: string | null,
  currentMaterialSource: string | null,
): { finish_id: string | null; material_source?: string | null } {
  if (newFinishId) return { finish_id: newFinishId, material_source: SWATCH_MATERIAL_SOURCE };
  return currentMaterialSource === SWATCH_MATERIAL_SOURCE
    ? { finish_id: null, material_source: null }
    : { finish_id: null };
}

/**
 * Warning shown next to the selector. Swatch mode makes the pipeline skip PBR (no Stability
 * texture), which is the point for native CAD but changes the result for anything else.
 */
export function swatchModeWarning(
  geometrySource: string | null | undefined,
  materialSource: string | null | undefined,
): string | null {
  const parts: string[] = [];
  if (!isNativeCad(geometrySource)) {
    parts.push(
      `This product's geometry source is ${geometrySource ? `"${geometrySource}"` : "not set"}, not dxf_extrusion / rfa_cad. ` +
        "Swatch mode skips PBR (no generated texture), so it is meant for native-CAD products. " +
        "Only choose a finish here if you want a flat finish instead of the generated material.",
    );
  }
  if (materialSource && materialSource !== SWATCH_MATERIAL_SOURCE) {
    parts.push(
      `Its material source is currently "${materialSource}". Choosing a finish replaces it with "${SWATCH_MATERIAL_SOURCE}"; ` +
        "clearing the finish later will not restore it.",
    );
  }
  return parts.length ? parts.join(" ") : null;
}

export interface AssemblyRerunState {
  allowed: boolean;
  reason: string | null;
}

/** Whether "Re-run assembly" can work right now, mirroring trigger-glb-assembly's own checks. */
export function assemblyRerun(p: {
  pbr_status: string | null;
  glb_assembly_status: string | null;
}): AssemblyRerunState {
  if ((ASSEMBLY_BUSY_STATES as readonly string[]).includes(p.glb_assembly_status ?? "")) {
    return { allowed: false, reason: `Assembly is already ${p.glb_assembly_status}.` };
  }
  if (!(ASSEMBLY_PBR_STATES as readonly string[]).includes(p.pbr_status ?? "")) {
    return {
      allowed: false,
      reason:
        `Assembly needs PBR to be complete, complete_fallback or review (currently ${p.pbr_status ?? "not run"}). ` +
        "Use Re-run Full Pipeline instead.",
    };
  }
  return { allowed: true, reason: null };
}
